import Link from '../../components/framework/link'
import { House } from 'lucide-react'
import React from 'react'
import { SITE } from '../../config/site'

interface BrandLogoProps {
  /** Slightly tighter treatment for the drawer header. */
  size?: 'md' | 'sm'
  /** `light` is the same mark inverted for navy surfaces, e.g. the footer. */
  tone?: 'dark' | 'light'
  className?: string
}

/**
 * Temporary brand treatment — a navy mark holding a house glyph, beside a
 * single-line wordmark. Deliberately plain so a real logo asset can replace it
 * without disturbing the header's layout: swap the contents of this file only.
 */
export function BrandLogo({ size = 'md', tone = 'dark', className = '' }: BrandLogoProps) {
  // The header treatment steps down one notch on the narrowest phones so the
  // wordmark and the menu button never crowd each other at 320px.
  const mark = size === 'md' ? 'size-9 sm:size-10' : 'size-9'
  const glyph = size === 'md' ? 'size-[1.125rem] sm:size-5' : 'size-[1.125rem]'
  const word = size === 'md' ? 'text-base sm:text-[1.0625rem]' : 'text-base'

  // The mark keeps its geometry in both tones; only the surface flips, so the
  // navy tile does not disappear into a navy background.
  const tile = tone === 'light' ? 'bg-white/10 ring-1 ring-white/15' : 'bg-navy'
  const wordmark = tone === 'light' ? 'text-white' : 'text-navy'

  return (
    <Link
      href="/"
      className={`inline-flex items-center gap-2.5 rounded-btn ${className}`}
      aria-label={`${SITE.name} — home`}
    >
      <span
        className={`${mark} ${tile} grid shrink-0 place-items-center rounded-btn`}
        aria-hidden="true"
      >
        <House className={`${glyph} text-orange`} strokeWidth={2} />
      </span>
      <span className={`${word} ${wordmark} font-bold tracking-[-0.02em]`}>{SITE.name}</span>
    </Link>
  )
}
